import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';

// ── Incoterm-Stammdaten ────────────────────────────────────────────────────────

const INCOTERM_META = {
  EXW: { label: 'EXW — Ex Works',                   group: 'Abgangsklauseln',  insuranceBearer: 'buyer',  riskTransfer: 'Ab Verkäuferwerk' },
  FCA: { label: 'FCA — Free Carrier',                group: 'Abgangsklauseln',  insuranceBearer: 'buyer',  riskTransfer: 'Übergabe an Frachtführer am genannten Ort' },
  CPT: { label: 'CPT — Carriage Paid To',            group: 'Abgangsklauseln',  insuranceBearer: 'buyer',  riskTransfer: 'Übergabe an ersten Frachtführer' },
  CIP: { label: 'CIP — Carriage & Insurance Paid',   group: 'Abgangsklauseln',  insuranceBearer: 'seller', riskTransfer: 'Übergabe an ersten Frachtführer' },
  DAP: { label: 'DAP — Delivered At Place',          group: 'Ankunftsklauseln', insuranceBearer: 'seller', riskTransfer: 'Am Bestimmungsort, nicht entladen' },
  DPU: { label: 'DPU — Delivered at Place Unloaded', group: 'Ankunftsklauseln', insuranceBearer: 'seller', riskTransfer: 'Am Bestimmungsort, nach Entladung' },
  DDP: { label: 'DDP — Delivered Duty Paid',         group: 'Ankunftsklauseln', insuranceBearer: 'seller', riskTransfer: 'Am Bestimmungsort, verzollt' },
  FAS: { label: 'FAS — Free Alongside Ship',         group: 'See-/Binnenschiff', insuranceBearer: 'buyer', riskTransfer: 'Längsseits Schiff im Verschiffungshafen' },
  FOB: { label: 'FOB — Free On Board',               group: 'See-/Binnenschiff', insuranceBearer: 'buyer', riskTransfer: 'Übergang nach Bord des Schiffes im Verschiffungshafen' },
  CFR: { label: 'CFR — Cost and Freight',            group: 'See-/Binnenschiff', insuranceBearer: 'buyer', riskTransfer: 'Nach Bord des Schiffes im Verschiffungshafen' },
  CIF: { label: 'CIF — Cost, Insurance & Freight',   group: 'See-/Binnenschiff', insuranceBearer: 'seller', riskTransfer: 'Nach Bord des Schiffes im Verschiffungshafen' },
};

const INCOTERM_CODES = Object.keys(INCOTERM_META);

const DEFAULT_RESPONSIBILITIES = {
  FOB: {
    seller: ['Ware exportverzollt am Abgangsort bereitstellen', 'Kosten bis Übergabe an Bord', 'Exportzoll und -formalitäten', 'Verpackung und Kennzeichnung'],
    buyer:  ['Seefrachtkosten ab Verladehafen', 'Versicherung (nicht pflichtgemäß)', 'Importzoll und -formalitäten', 'Entladung am Bestimmungsort'],
  },
  CIF: {
    seller: ['Ware exportverzollt bereitstellen', 'Seefrachtkosten bis Bestimmungshafen', 'Mindestversicherung (ICC-C)', 'Exportzoll und -formalitäten', 'Verpackung und Kennzeichnung'],
    buyer:  ['Importzoll und -formalitäten', 'Entladung am Bestimmungshafen', 'Transport ab Bestimmungshafen', 'Zusatzversicherung bei Bedarf'],
  },
};

const COMPLIANCE_COLORS = {
  compliant:     { bg: '#22c55e15', border: '#22c55e40', text: '#22c55e', label: 'Konform' },
  partial:       { bg: '#f59e0b15', border: '#f59e0b40', text: '#f59e0b', label: 'Teilweise konform' },
  non_compliant: { bg: '#ef444415', border: '#ef444440', text: '#ef4444', label: 'Nicht konform' },
  not_assessed:  { bg: '#60706015', border: '#60706040', text: 'var(--text3)', label: 'Nicht bewertet' },
};

const INSURANCE_BEARER_LABEL = { seller: 'Verkäufer', buyer: 'Käufer', none: 'Keine', '': '—' };

// ── Badge-Komponenten ─────────────────────────────────────────────────────────

function ComplianceBadge({ status }) {
  const c = COMPLIANCE_COLORS[status] || COMPLIANCE_COLORS.not_assessed;
  return (
    <span style={{
      padding: '2px 10px', borderRadius: 99,
      background: c.bg, border: `1px solid ${c.border}`,
      color: c.text, fontSize: 11, fontWeight: 600,
    }}>
      {c.label}
    </span>
  );
}

function InsuranceBadge({ required, bearer }) {
  if (!required) return <span style={{ color: 'var(--text3)', fontSize: 12 }}>Nicht erforderlich</span>;
  return (
    <span style={{
      padding: '2px 10px', borderRadius: 99,
      background: '#3b82f615', border: '1px solid #3b82f640',
      color: '#3b82f6', fontSize: 11, fontWeight: 600,
    }}>
      Pflicht · {INSURANCE_BEARER_LABEL[bearer] || '—'}
    </span>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

function IncotermModal({ entry, onSave, onClose }) {
  const defaults = entry ? {
    incoterm_code:           entry.incoterm_code,
    incoterm_version:        entry.incoterm_version || '2020',
    trade_type:              entry.trade_type || 'both',
    risk_transfer_point:     entry.risk_transfer_point || '',
    seller_responsibilities: (entry.seller_responsibilities || []).join('\n'),
    buyer_responsibilities:  (entry.buyer_responsibilities || []).join('\n'),
    insurance_required:      entry.insurance_required || false,
    insurance_bearer:        entry.insurance_bearer || '',
    insurance_notes:         entry.insurance_notes || '',
    legal_implications:      entry.legal_implications || '',
    compliance_status:       entry.compliance_status || 'not_assessed',
    compliance_notes:        entry.compliance_notes || '',
    applicable_deals:        (entry.applicable_deals || []).join(', '),
    notes:                   entry.notes || '',
  } : {
    incoterm_code: 'FOB',
    incoterm_version: '2020',
    trade_type: 'both',
    risk_transfer_point: '',
    seller_responsibilities: '',
    buyer_responsibilities: '',
    insurance_required: false,
    insurance_bearer: '',
    insurance_notes: '',
    legal_implications: '',
    compliance_status: 'not_assessed',
    compliance_notes: '',
    applicable_deals: '',
    notes: '',
  };

  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCodeChange = (code) => {
    set('incoterm_code', code);
    const meta = INCOTERM_META[code];
    if (!entry && meta) {
      set('risk_transfer_point', meta.riskTransfer || '');
      set('insurance_required', meta.insuranceBearer === 'seller');
      set('insurance_bearer', meta.insuranceBearer || '');
    }
    const defs = DEFAULT_RESPONSIBILITIES[code];
    if (!entry && defs) {
      set('seller_responsibilities', defs.seller.join('\n'));
      set('buyer_responsibilities', defs.buyer.join('\n'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const payload = {
      incoterm_code:           form.incoterm_code,
      incoterm_version:        form.incoterm_version,
      trade_type:              form.trade_type,
      risk_transfer_point:     form.risk_transfer_point || null,
      seller_responsibilities: form.seller_responsibilities.split('\n').map(s => s.trim()).filter(Boolean),
      buyer_responsibilities:  form.buyer_responsibilities.split('\n').map(s => s.trim()).filter(Boolean),
      insurance_required:      form.insurance_required,
      insurance_bearer:        form.insurance_bearer || null,
      insurance_notes:         form.insurance_notes || null,
      legal_implications:      form.legal_implications || null,
      compliance_status:       form.compliance_status,
      compliance_notes:        form.compliance_notes || null,
      applicable_deals:        form.applicable_deals.split(',').map(s => s.trim()).filter(Boolean),
      notes:                   form.notes || null,
      tags:                    [],
    };
    try {
      if (entry) {
        await API.patch(`/incoterms/${entry.id}`, payload);
      } else {
        await API.post('/incoterms/', payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = {
    width: '100%', padding: '8px 10px',
    background: '#0f1a0f', border: '1px solid var(--border)', color: 'var(--text1)',
    borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' };
  const sectionStyle = { marginBottom: 20 };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 12, width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--text1)' }}>
            {entry ? 'Incoterm bearbeiten' : 'Neuen Incoterm anlegen'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 20 }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
          {error && (
            <div style={{ padding: '10px 14px', background: '#ef444415', border: '1px solid #ef444440', borderRadius: 6, color: '#ef4444', fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, ...sectionStyle }}>
            <div>
              <label style={labelStyle}>Incoterm *</label>
              <select value={form.incoterm_code} onChange={e => handleCodeChange(e.target.value)} style={fieldStyle} required>
                {INCOTERM_CODES.map(c => <option key={c} value={c}>{INCOTERM_META[c]?.label || c}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Version</label>
              <select value={form.incoterm_version} onChange={e => set('incoterm_version', e.target.value)} style={fieldStyle}>
                <option value="2020">Incoterms® 2020</option>
                <option value="2010">Incoterms® 2010</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Handelsrichtung</label>
              <select value={form.trade_type} onChange={e => set('trade_type', e.target.value)} style={fieldStyle}>
                <option value="both">Import & Export</option>
                <option value="export">Export</option>
                <option value="import">Import</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Anwendbare Deals (kommagetrennt)</label>
              <input type="text" value={form.applicable_deals} onChange={e => set('applicable_deals', e.target.value)} style={fieldStyle} placeholder="Deal A, Deal B, ..." />
            </div>
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Risikoübergangspunkt</label>
            <input type="text" value={form.risk_transfer_point} onChange={e => set('risk_transfer_point', e.target.value)} style={fieldStyle} placeholder="z.B. Übergabe nach Bord des Schiffes im Verschiffungshafen" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, ...sectionStyle }}>
            <div>
              <label style={labelStyle}>Pflichten des Verkäufers (eine pro Zeile)</label>
              <textarea value={form.seller_responsibilities} onChange={e => set('seller_responsibilities', e.target.value)} style={{ ...fieldStyle, height: 120, resize: 'vertical' }} placeholder="Ware exportverzollt bereitstellen&#10;Verpackung und Kennzeichnung&#10;..." />
            </div>
            <div>
              <label style={labelStyle}>Pflichten des Käufers (eine pro Zeile)</label>
              <textarea value={form.buyer_responsibilities} onChange={e => set('buyer_responsibilities', e.target.value)} style={{ ...fieldStyle, height: 120, resize: 'vertical' }} placeholder="Frachtkosten ab Verschiffungshafen&#10;Importzoll und -formalitäten&#10;..." />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 16, alignItems: 'end', ...sectionStyle }}>
            <div>
              <label style={labelStyle}>Versicherungspflicht</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.insurance_required} onChange={e => set('insurance_required', e.target.checked)} />
                <span style={{ fontSize: 13, color: 'var(--text1)' }}>Pflichtversicherung</span>
              </label>
            </div>
            <div>
              <label style={labelStyle}>Versicherungsträger</label>
              <select value={form.insurance_bearer} onChange={e => set('insurance_bearer', e.target.value)} style={fieldStyle} disabled={!form.insurance_required}>
                <option value="">— wählen —</option>
                <option value="seller">Verkäufer</option>
                <option value="buyer">Käufer</option>
                <option value="none">Keine</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Versicherungshinweise</label>
              <input type="text" value={form.insurance_notes} onChange={e => set('insurance_notes', e.target.value)} style={fieldStyle} placeholder="z.B. ICC-C Mindestdeckung" disabled={!form.insurance_required} />
            </div>
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Rechtliche Implikationen</label>
            <textarea value={form.legal_implications} onChange={e => set('legal_implications', e.target.value)} style={{ ...fieldStyle, height: 90, resize: 'vertical' }} placeholder="Relevante rechtliche Anforderungen, Risiken und Hinweise..." />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, ...sectionStyle }}>
            <div>
              <label style={labelStyle}>Compliance-Status</label>
              <select value={form.compliance_status} onChange={e => set('compliance_status', e.target.value)} style={fieldStyle}>
                <option value="not_assessed">Nicht bewertet</option>
                <option value="compliant">Konform</option>
                <option value="partial">Teilweise konform</option>
                <option value="non_compliant">Nicht konform</option>
              </select>
            </div>
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Compliance-Hinweise</label>
            <textarea value={form.compliance_notes} onChange={e => set('compliance_notes', e.target.value)} style={{ ...fieldStyle, height: 70, resize: 'vertical' }} placeholder="Befunde, offene Punkte, Maßnahmen..." />
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Notizen</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} style={{ ...fieldStyle, height: 70, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{
              padding: '8px 18px', borderRadius: 6, border: '1px solid var(--border)',
              background: 'var(--bg-card)', color: 'var(--text2)', cursor: 'pointer', fontSize: 13,
            }}>Abbrechen</button>
            <button type="submit" disabled={saving} style={{
              padding: '8px 18px', borderRadius: 6, border: 'none',
              background: '#22c55e', color: '#000', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}>
              {saving ? 'Speichern...' : (entry ? 'Speichern' : 'Anlegen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Verantwortungsmatrix ──────────────────────────────────────────────────────

function ResponsibilityMatrix({ entries }) {
  if (!entries.length) return (
    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
      Keine Einträge vorhanden
    </div>
  );

  const cellStyle = (isHeader) => ({
    padding: '8px 12px', fontSize: 12,
    background: isHeader ? '#0f1a0f' : 'transparent',
    fontWeight: isHeader ? 600 : 400,
    color: isHeader ? 'var(--text2)' : 'var(--text1)',
    borderBottom: '1px solid var(--border)',
    verticalAlign: 'top',
  });

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ ...cellStyle(true), width: 80 }}>Incoterm</th>
            <th style={{ ...cellStyle(true), width: 120 }}>Risikoübergang</th>
            <th style={cellStyle(true)}>Verkäuferpflichten</th>
            <th style={cellStyle(true)}>Käuferpflichten</th>
            <th style={{ ...cellStyle(true), width: 130 }}>Versicherung</th>
            <th style={{ ...cellStyle(true), width: 110 }}>Compliance</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(e => (
            <tr key={e.id}>
              <td style={cellStyle(false)}>
                <span style={{ fontWeight: 700, color: '#22c55e', fontSize: 13 }}>{e.incoterm_code}</span>
                <div style={{ color: 'var(--text3)', fontSize: 11 }}>{e.incoterm_version}</div>
              </td>
              <td style={cellStyle(false)}>
                <span style={{ color: 'var(--text2)', fontSize: 11 }}>{e.risk_transfer_point || '—'}</span>
              </td>
              <td style={cellStyle(false)}>
                {(e.seller_responsibilities || []).length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 14 }}>
                    {(e.seller_responsibilities || []).map((r, i) => <li key={i} style={{ marginBottom: 2 }}>{r}</li>)}
                  </ul>
                ) : <span style={{ color: 'var(--text3)' }}>—</span>}
              </td>
              <td style={cellStyle(false)}>
                {(e.buyer_responsibilities || []).length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 14 }}>
                    {(e.buyer_responsibilities || []).map((r, i) => <li key={i} style={{ marginBottom: 2 }}>{r}</li>)}
                  </ul>
                ) : <span style={{ color: 'var(--text3)' }}>—</span>}
              </td>
              <td style={cellStyle(false)}>
                <InsuranceBadge required={e.insurance_required} bearer={e.insurance_bearer} />
                {e.insurance_notes && (
                  <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 4 }}>{e.insurance_notes}</div>
                )}
              </td>
              <td style={cellStyle(false)}>
                <ComplianceBadge status={e.compliance_status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Detailansicht ─────────────────────────────────────────────────────────────

function IncotermDetail({ entry, onEdit, onDelete, onBack }) {
  const meta = INCOTERM_META[entry.incoterm_code] || {};
  const cc = COMPLIANCE_COLORS[entry.compliance_status] || COMPLIANCE_COLORS.not_assessed;

  const cardStyle = {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 10, padding: '16px 20px', marginBottom: 16,
  };
  const sectionTitle = { fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12 };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 13, padding: 0 }}>
          ← Zurück
        </button>
        <h2 style={{ margin: 0, fontSize: 18, color: 'var(--text1)' }}>{entry.incoterm_code} — {meta.label?.split(' — ')[1] || entry.incoterm_code}</h2>
        <span style={{ fontSize: 11, color: 'var(--text3)', background: '#0f1a0f', border: '1px solid var(--border)', padding: '2px 8px', borderRadius: 99 }}>
          Incoterms® {entry.incoterm_version}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={onEdit} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', background: '#0f1a0f', color: 'var(--text2)', cursor: 'pointer', fontSize: 12 }}>
            Bearbeiten
          </button>
          <button onClick={onDelete} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ef444440', background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>
            Löschen
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={sectionTitle}>Risikoallokation</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text2)' }}>Risikoübergangspunkt</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: entry.risk_transfer_point ? 'normal' : 'italic' }}>
              {entry.risk_transfer_point || 'Nicht angegeben'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text2)' }}>Incoterm-Gruppe</div>
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>{meta.group || '—'}</div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={sectionTitle}>Verantwortungsmatrix</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
              Verkäuferpflichten
            </div>
            {(entry.seller_responsibilities || []).length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {(entry.seller_responsibilities || []).map((r, i) => (
                  <li key={i} style={{ fontSize: 13, marginBottom: 6, color: 'var(--text1)' }}>{r}</li>
                ))}
              </ul>
            ) : <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>Keine Angaben</div>}
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />
              Käuferpflichten
            </div>
            {(entry.buyer_responsibilities || []).length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {(entry.buyer_responsibilities || []).map((r, i) => (
                  <li key={i} style={{ fontSize: 13, marginBottom: 6, color: 'var(--text1)' }}>{r}</li>
                ))}
              </ul>
            ) : <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>Keine Angaben</div>}
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={sectionTitle}>Versicherungsanforderungen</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: entry.insurance_notes ? 12 : 0 }}>
          <InsuranceBadge required={entry.insurance_required} bearer={entry.insurance_bearer} />
          {entry.insurance_bearer && (
            <span style={{ fontSize: 13, color: 'var(--text3)' }}>
              Träger: <strong style={{ color: 'var(--text1)' }}>{INSURANCE_BEARER_LABEL[entry.insurance_bearer]}</strong>
            </span>
          )}
        </div>
        {entry.insurance_notes && (
          <div style={{ fontSize: 13, color: 'var(--text1)', marginTop: 8, padding: '10px 12px', background: '#3b82f615', borderRadius: 6, border: '1px solid #3b82f630' }}>
            {entry.insurance_notes}
          </div>
        )}
      </div>

      {entry.legal_implications && (
        <div style={cardStyle}>
          <div style={sectionTitle}>Rechtliche Implikationen</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {entry.legal_implications}
          </div>
        </div>
      )}

      <div style={{ ...cardStyle, border: `1px solid ${cc.border}`, background: cc.bg }}>
        <div style={sectionTitle}>Compliance-Bewertung</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: entry.compliance_notes ? 12 : 0 }}>
          <ComplianceBadge status={entry.compliance_status} />
          {(entry.applicable_deals || []).length > 0 && (
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>
              Deals: {entry.applicable_deals.join(', ')}
            </span>
          )}
        </div>
        {entry.compliance_notes && (
          <div style={{ fontSize: 13, color: cc.text, padding: '10px 12px', background: 'var(--bg-app)', borderRadius: 6, border: `1px solid ${cc.border}` }}>
            {entry.compliance_notes}
          </div>
        )}
      </div>

      {entry.notes && (
        <div style={cardStyle}>
          <div style={sectionTitle}>Notizen</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{entry.notes}</div>
        </div>
      )}
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────────

export default function IncotermManagementPage() {
  const [entries,   setEntries]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [view,      setView]      = useState('list');  // list | matrix | detail
  const [selected,  setSelected]  = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [filterCode, setFilterCode] = useState('');

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/incoterms/');
      setEntries(r.data || []);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const handleSave = () => {
    setShowModal(false);
    setEditEntry(null);
    loadEntries();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    await API.delete(`/incoterms/${id}`);
    setSelected(null);
    setView('list');
    loadEntries();
  };

  const filtered = filterCode ? entries.filter(e => e.incoterm_code === filterCode) : entries;

  const countByStatus = (s) => entries.filter(e => e.compliance_status === s).length;
  const insuranceReq = entries.filter(e => e.insurance_required).length;

  const kpiCardStyle = {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 10, padding: '14px 18px',
    display: 'flex', flexDirection: 'column', gap: 4,
  };

  return (
    <div style={{ maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text1)' }}>📋 Incoterm Management</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text3)' }}>
            FOB/CIF Risikoallokation · Verantwortungsmatrix · Versicherungsanforderungen · Rechtliche Implikationen
          </p>
        </div>
        <button onClick={() => { setEditEntry(null); setShowModal(true); }} style={{
          padding: '9px 18px', borderRadius: 8, border: 'none',
          background: '#22c55e', color: '#000', cursor: 'pointer', fontSize: 13, fontWeight: 600,
        }}>
          + Incoterm anlegen
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div style={kpiCardStyle}>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gesamt</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text1)' }}>{entries.length}</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Konform</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#22c55e' }}>{countByStatus('compliant')}</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teilweise</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#f59e0b' }}>{countByStatus('partial')}</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nicht konform</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#ef4444' }}>{countByStatus('non_compliant')}</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Versicherungspfl.</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#3b82f6' }}>{insuranceReq}</div>
        </div>
      </div>

      {/* Detail-Ansicht */}
      {view === 'detail' && selected && (
        <IncotermDetail
          entry={selected}
          onEdit={() => { setEditEntry(selected); setShowModal(true); }}
          onDelete={() => handleDelete(selected.id)}
          onBack={() => { setView('list'); setSelected(null); }}
        />
      )}

      {/* Listen- / Matrix-Ansicht */}
      {view !== 'detail' && (
        <>
          {/* Toolbar */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
              {['list', 'matrix'].map(v => (
                <button key={v} onClick={() => setView(v)} style={{
                  padding: '7px 16px', border: 'none', cursor: 'pointer', fontSize: 12,
                  background: view === v ? '#22c55e' : 'var(--bg-card)',
                  color: view === v ? '#000' : 'var(--text3)',
                  fontWeight: view === v ? 600 : 400,
                }}>
                  {v === 'list' ? 'Karten' : 'Matrix'}
                </button>
              ))}
            </div>
            <select value={filterCode} onChange={e => setFilterCode(e.target.value)} style={{
              padding: '7px 12px', borderRadius: 6, border: '1px solid var(--border)',
              background: 'var(--bg-card)', color: 'var(--text1)', fontSize: 12,
            }}>
              <option value="">Alle Incoterms</option>
              {INCOTERM_CODES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>{filtered.length} Einträge</span>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Lade Daten...</div>
          ) : filtered.length === 0 ? (
            <div style={{
              padding: '40px 20px', textAlign: 'center', background: 'var(--bg-card)',
              border: '1px solid var(--border)', borderRadius: 10,
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📦</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'var(--text1)' }}>Keine Incoterm-Einträge</div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20 }}>
                Legen Sie Incoterms an, um FOB/CIF-Risikoallokation und Verantwortlichkeiten zu dokumentieren.
              </div>
              <button onClick={() => { setEditEntry(null); setShowModal(true); }} style={{
                padding: '8px 18px', borderRadius: 8, border: 'none',
                background: '#22c55e', color: '#000', cursor: 'pointer', fontSize: 13,
              }}>
                Ersten Incoterm anlegen
              </button>
            </div>
          ) : view === 'matrix' ? (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text3)' }}>
                Verantwortungsmatrix — {filtered.length} Incoterm{filtered.length !== 1 ? 's' : ''}
              </div>
              <ResponsibilityMatrix entries={filtered} />
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
              {filtered.map(e => {
                const meta = INCOTERM_META[e.incoterm_code] || {};
                return (
                  <div key={e.id} onClick={() => { setSelected(e); setView('detail'); }} style={{
                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: 10, padding: '16px 18px',
                    cursor: 'pointer',
                  }}
                    onMouseEnter={ev => ev.currentTarget.style.borderColor = '#22c55e60'}
                    onMouseLeave={ev => ev.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <span style={{ fontSize: 20, fontWeight: 800, color: '#22c55e' }}>{e.incoterm_code}</span>
                        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>{e.incoterm_version}</span>
                      </div>
                      <ComplianceBadge status={e.compliance_status} />
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
                      {meta.label?.split(' — ')[1] || meta.group || ''}
                    </div>
                    {e.risk_transfer_point && (
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8, padding: '6px 10px', background: '#0f1a0f', borderRadius: 6, border: '1px solid var(--border)' }}>
                        <span style={{ color: 'var(--text3)', fontSize: 11 }}>Risikoübergang: </span>
                        {e.risk_transfer_point}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      <InsuranceBadge required={e.insurance_required} bearer={e.insurance_bearer} />
                      {e.trade_type !== 'both' && (
                        <span style={{ padding: '2px 8px', borderRadius: 99, background: '#0f1a0f', border: '1px solid var(--border)', fontSize: 11, color: 'var(--text3)' }}>
                          {e.trade_type === 'export' ? 'Export' : 'Import'}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                        {(e.seller_responsibilities || []).length + (e.buyer_responsibilities || []).length} Pflichten definiert
                      </span>
                      <button onClick={ev => { ev.stopPropagation(); setEditEntry(e); setShowModal(true); }} style={{
                        padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border)',
                        background: 'transparent', color: 'var(--text3)', cursor: 'pointer', fontSize: 11,
                      }}>
                        Bearbeiten
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <IncotermModal
          entry={editEntry}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditEntry(null); }}
        />
      )}
    </div>
  );
}
